import React, { useState, useEffect } from "react";
import { useOverviewContext } from "../context/OverviewContext";
import learningCommunityImg from '../assets/images/LearningCoachCommunity_Vertical.png';
import contactImg from '../assets/images/contact-globe-overview.svg';
import emojiIcon from '../assets/images/overviewEmojiIcon.svg';
import bgIcon1 from '../assets/images/siteImg/sec-4-bg-icon-1.svg';
import bgIcon2 from '../assets/images/siteImg/sec-4-bg-icon-2.svg';
import checklistIconImg from '../assets/images/siteImg/sec-3-icon-checklist.svg';
import suppliesIconImg from '../assets/images/siteImg/icon-overview-supplies.svg';
import communityIconImg from '../assets/images/siteImg/sec-5-icon-community.svg';
import contactIconImg from '../assets/images/siteImg/sec-5-icon-contact.svg';
import formsIcon from '../assets/images/siteImg/icon-checklist-forms-and-documents.svg';
import learningCoachIcon from '../assets/images/siteImg/icon-checklist-learning-coach-life.svg';
import onlineSchoolIcon from '../assets/images/siteImg/icon-checklist-online-school.svg';
import schoolEmailIcon from '../assets/images/siteImg/icon-checklist-school-email.svg';
import checkListImg from '../assets/images/siteImg/laptop-checkmark.svg';
import bannerOverviewImage from '../assets/images/banners/bannerImage_overview.png';
import "../assets/css/home.css";
import { useChecklist } from "../context/ChecklistContext";
import VidyardPlayer from "../components/VidyardPlayer";
import { getActiveToolIDs, shouldShowK12Zone, shouldShowLearningCoachCommunity } from "../utils/menuUtils";
import { useToolSettings } from "../context/ToolSettingsContext";
import PopupSlider from "../components/PopupSlider";
import ImageContent from "../components/ImageContent";
import ButtonLink from "../components/ButtonLink";

export function Home() {
  const percentage = 0.5;
  const { loggedInUser, students, schoolFullName, formattedStudentNames, hasNonHighSchoolStudents,  loading, error } = useOverviewContext();
  const { checklistData, checklistTopicStatus } = useChecklist();
  
  const [schoolLabel, setSchoolLabel] = useState("");
  const [schoolDaysLabel, setSchoolDaysLabel] = useState("");

  const SCHOOL_STARTED_LABEL = "School has started";
  const getSchoolStartingSoonLabel = (days: number) => `${days} days until school starts!`;

  const decodeJWT = (token: string) => {
    try {
      const base64Url = token.split(".")[1]; 
      return JSON.parse(atob(base64Url)); 
    } catch (error) {
      console.error("Error decoding authToken", error);
      return null;
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      const decodedToken = decodeJWT(token);
      if (decodedToken) {
        const datesArray = decodedToken?.claim?.dates;
        if (Array.isArray(datesArray) && datesArray.length > 0 && datesArray[0]?.schoolStartDate) {
          const schoolStartDate = new Date(datesArray[0].schoolStartDate);
          const currentDate = new Date();
          const diffDays = Math.ceil((schoolStartDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));

          setSchoolLabel(diffDays > 0 ? getSchoolStartingSoonLabel(diffDays) : SCHOOL_STARTED_LABEL);
          setSchoolDaysLabel(`${diffDays}`);
        } else {
          setSchoolLabel(SCHOOL_STARTED_LABEL);
        }
      }
    }
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;


// Start checklistLogic section-3 
// console.log("Checklist Data:", checklistData);
// console.log("Checklist Topic Status:", checklistTopicStatus);

// Group checklist items by category and count topics dynamically
const categoryMap = new Map<string, { checklistName: string; topicCount: number }>();
const userRole = loggedInUser?.role || null;
checklistData
  .filter((item) => item.roles.includes(userRole) && (hasNonHighSchoolStudents || item.checklistName !== "CONSENT_PROCESS")) 
  .forEach((item) => {
    if (!categoryMap.has(item.checklistCategoryName)) {
      categoryMap.set(item.checklistCategoryName, { checklistName: item.checklistName, topicCount: 0 });
    }
    categoryMap.get(item.checklistCategoryName)!.topicCount += 1; 
  });

  // Extract completed checklist IDs from checklistTopicStatus percentageLogic
  const completedChecklistIds = checklistTopicStatus
    .filter(item => item.status === "true")
    .map(item => item.checklistId);

  // Category-to-Icon Mapping
  const categoryIcons: { [key: string]: string } = {
    "forms-and-documents": formsIcon,
    "learning-coach-life": learningCoachIcon,
    "online-school": onlineSchoolIcon,
    "school-email": schoolEmailIcon
  };
  const { toolSettings } = useToolSettings();
  const activeToolIDs = getActiveToolIDs(toolSettings);
  const showLearningCoachCommunity = shouldShowLearningCoachCommunity(userRole, activeToolIDs);
  const showK12Zone = shouldShowK12Zone(userRole, activeToolIDs); 
  // console.log(showLearningCoachCommunity, showK12Zone);

  return (
    <div className="main-content" id="overview-page">
      <div className="row d-block d-xl-none">
          <div className="col-12">
            <nav aria-label="breadcrumb" className="d-none d-sm-block">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <span>Home</span>  
                  </li>
                </ol>
            </nav>
          </div>
      </div>
      {/* Initial Popup */}
      <PopupSlider />

      {/* section-1 */}
      <div className="wp-block-k12-section container-fluid rounded-top-corners bg-color-blue-2" id="section-1">
        <div className="row">
          <div className="col-12 g-0">
            <div className="wp-block-k12-user-role-content-wrapper">
              <div className="wp-block-k12-hero">
                <div className="row no-wrap">
                  <div className="col-sm-12 col-md-6 col-xl-6 col-xxl-7 align-self-center">
                    <h1>
                      Hello, {loggedInUser?.fname}{" "}
                      <img
                        draggable="false"
                        role="img"
                        className="emoji"
                        alt="overviewEmoji"
                        src={emojiIcon}
                      />
                    </h1>
                    <p className="heading-4 heading-4-banner">
                      <strong>
                        {loggedInUser.role === "LEARNING_COACH"
                          ? `We're glad you're here! As the Learning Coach for ${formattedStudentNames}, you can access these resources to help them transition to **${schoolFullName}. Let's get set for a great school start!`
                          : `We're glad you're here! We've put together the things you need to know to get off to a great start at ${schoolFullName}. Let's go!`}
                      </strong>
                    </p>

                  </div>

                  <div className="col-sm-12 col-md-6 col-xl-6 col-xxl-4 justify-content-center d-flex">
                    <img
                      width="1080"
                      height="624"
                      src={bannerOverviewImage}
                      alt=""
                      className="wp-image-574 img-fluid"
                      sizes="(max-width: 1080px) 100vw, 1080px"
                    />
                    <div className="circle-background"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* section-2 */}
      <div className="wp-block-k12-section container-fluid bg-transparent">
        <div className="row">
          <div className="col-12 g-0">
            <div className="wp-block-k12-landing-header">
              <div className="row countdown-row g-3" id="section-2">
                {students.length === 1 ? (
                  // Full-width layout for a single student
                  students.map((student, index) => (
                    <div className="col-12 col-lg-12" data-sequence={index}>
                      <div className="shadow h-100">
                        <div className="row g-0 h-100">
                          <div className="col-lg-12 col-xl-8">
                          <div className="countdown bg-color-light-gray-2 d-flex align-items-center justify-content-center h-100">
                              {schoolDaysLabel ? (
                                <>
                                  <span className="days">{schoolDaysLabel}</span>
                                  <div>
                                    <h3>
                                      <span className="regular">days until school starts!</span>
                                    </h3>
                                  </div>
                                </>
                              ) : (
                                <h3 className="primary-color2">{schoolLabel}</h3>
                              )}
                            </div>
                         
                          </div>
                          <div className="col-lg-12 col-xl-4">
                            <div className="student d-flex flex-column flex-md-row flex-lg-column justify-content-center justify-content-md-between justify-content-lg-center align-items-center h-100 text-center">
                              <span className="name">
                                <strong>{student.firstName}</strong> {student.lastName}
                              </span>
                              <span className="grade">
                                Grade {student.grade.gradeId} | {student.schoolDetails?.shortName} (TEST, {loggedInUser.schoolId})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Multiple students
                  students.map((student, index) => (
                    <div className="col-12 col-lg-6" data-sequence={index}>
                      <div className="shadow h-100">
                        <div className="row g-0 h-100">
                          <div className="col-lg-12 col-xl-8">
                            <div className="countdown bg-color-light-gray-2 d-flex align-items-center justify-content-center h-100">
                              {schoolDaysLabel ? (
                                <>
                                  <span className="days">{schoolDaysLabel}</span>
                                  <div>
                                    <h3>
                                      <span className="regular">days until school starts!</span>
                                    </h3>
                                  </div>
                                </>
                              ) : (
                                <h3 className="primary-color">{schoolLabel}</h3>
                              )}
                            </div>

                          </div>
                          <div className="col-lg-12 col-xl-4">
                            <div className="student d-flex flex-column flex-md-row flex-lg-column justify-content-center justify-content-md-between justify-content-lg-center align-items-center h-100 text-center">
                              <span className="name">
                                <strong>{student.firstName}</strong> {student.lastName}
                              </span>
                              <span className="grade">
                                Grade {student.grade.gradeId} | {student.schoolDetails?.shortName}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* section-3 */}
      <div className="wp-block-k12-section container-fluid bg-color-blue-t1-checklist" id="section-3">
        <div className="row">
          <div className="col-12 g-0">
            <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-1 wp-block-columns-is-layout-flex">
              <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                <div className="wp-block-k12-cta-blade">
                  <div className="row">
                    <div className="col-12">
                      <div className="p-5">
                        <div className="d-flex gap-2 bottom-left padding-content">
                          <div className="align-items-center">
                            <img decoding="async" src={checklistIconImg} alt="" className="wp-image- img-fluid cta-icon" />
                          </div>
                          <div className="flex-grow-1 align-items-center">
                            <p className="eyebrow">School Prep Checklist</p>
                            <h2 className="primary-color">Let's get ready!</h2>
                            <p className="">
                              While you wait to hear from your school, here are the top things you can do now to hit the ground running once school starts!&nbsp;
                            </p>
                            <div className="btn-primary text-center">
                              <a href="/school-prep-checklist/" data-type="page" data-id="239">
                                View School Prep Checklist
                              </a>
                            </div>
                          </div>
                        </div>
                        {/* Reusable Img Content */}
                        <ImageContent
                            imageUrl={checkListImg}
                            imageAlt="checkListImg"
                            additionalClasses="bottom-left"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            

              {/* Checklist items */}
              <div className="wp-block-column d-flex align-items-center is-layout-flow wp-block-column-is-layout-flow">
                <div className="wp-block-k12-landing-checklist container-fluid">
                  <div className="row">
                    <div className="col-12">
                      <div className="container">
                        <div className="row">
                          <div className="col-12">
                          <ul>
                            {Array.from(categoryMap).map(([category, { checklistName, topicCount }]) => {
                                // Format category names
                                const formattedChecklistName = checklistName.toLowerCase().replace(/_/g, "-");
                                const formattedCategory = category
                                  .toLowerCase()
                                  .replace(/_/g, " ")
                                  .split(" ")
                                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(" "); 

                                // const formattedCategory_url = category.toLowerCase().replace(/_/g, "-");

                                // Custom mapping for specific category names
                                const categoryUrlMapping: Record<string, string> = {
                                  "school_email": "email-setup",
                                };

                                // Apply mapping if exists, otherwise follow default logic
                                const formattedCategory_url = categoryUrlMapping[category.toLowerCase()] || category.toLowerCase().replace(/_/g, "-");

                                const categoryIcon = categoryIcons[formattedCategory_url] || onlineSchoolIcon; 
                                
                                // Get topics in this category & hide consent Process High School
                                const categoryTopics = checklistData.filter(item => item.checklistCategoryName === category && (hasNonHighSchoolStudents || item.checklistName !== "CONSENT_PROCESS") );

                                // Count completed topics
                                const completedTopics = categoryTopics.filter(topic => completedChecklistIds.includes(topic.checklistId)).length;
                                // Calculate percentage
                                const calc_percentage = topicCount > 0 ? Math.round((completedTopics / topicCount) * 100) : 0;
                                // console.log(categoryTopics, calc_percentage);

                                return (
                                  <li key={category} className="checklist-item" style={{ '--percentage': calc_percentage/100 } as React.CSSProperties} data-percentage={`${calc_percentage}`}>
                                    <div className="checklist-image">
                                      <img loading="lazy" decoding="async" width="40" height="40" src={categoryIcon} className="attachment-full size-full" alt="" />
                                    </div>
                                    <div className="checklist-body">
                                      <a href={`/k12-checklist-category/${formattedCategory_url}/`}>
                                        <h4>{formattedCategory}</h4>
                                      </a>
                                      <p className="text-small">{topicCount} {topicCount > 1 ? "Topics" : "Topic"}</p>
                                    </div>
                                    <div className="checklist-progress">
                                      <span>{calc_percentage}%</span>
                                    </div>
                                  </li>
                                );
                            })}
                          </ul>
      

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* section-4 */}
      <div className="wp-block-k12-section container-fluid  bg-transparent supplies" id="section-4">
        <div className="row" >
          <div className="col-12 g-0">
            <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-2 wp-block-columns-is-layout-flex">
              <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                <div className="wp-block-k12-cta-blade image-align-bottom-left">
                  <div className="row">
                    <div className="col-12">
                      <div className="p-3">
                        <div className="d-flex gap-2 bottom center">
                          <div className="align-items-center">
                            <img decoding="async" src={suppliesIconImg} alt="" className="wp-image- img-fluid cta-icon" />
                          </div>

                          <div className="flex-grow-1 align-items-center">
                            <p className="eyebrow">Learning Materials</p>
                            <h2 className="primary-color">Gear up for great learning!</h2>
                            <p className="">See what you might need and what you can expect to arrive at your door!</p>

                            <div className="btn-primary text-center">
                              <a href="/learning-materials/" data-type="page" data-id="246">Check Learning Materials</a>
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                <div className="wp-block-k12-section container-fluid  bg-color-yellow-t1-supplies d-flex justify-content-center align-items-center">
                  <div className="row">
                    <div className="col-12 g-0">
                      <div className="wp-block-k12-school-supplies">
                        <div className="h-100 students-list position-relative">
                          <div className="row h-100 gap-2 justify-content-center align-items-center">
                            <div className="col">
                              <div className="infocard">
                                <div className="tag">Coming Soon</div>
                                <div className="d-flex">
                                  <div className="student-name">
                                    <h4 className="fw-normal text-color-blue-3">
                                      <strong>{formattedStudentNames}</strong> Supplies</h4>
                                  </div>
                                  <div className="icon-box" style={{ backgroundImage: `url(${bgIcon2})` }}></div>
                                </div>
                                <div className="row g-1">
                                  <p>Check back for supply shipping updates, coming soon!</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-color-yellow-t1-supplies rounded-4 position-absolute background-element">
                          <div className="popout-image-container" style={{ backgroundImage: `url(${bgIcon1})` }}></div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* section-5 */}
      <div className="wp-block-k12-section container-fluid  bg-color-transparent" id="section-5">
        <div className="row">
          <div className="col-12 g-0">
            <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-4 wp-block-columns-is-layout-flex">
            {/* style={{ alignItems: "normal"}} */}
              <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
              
                  <div className="wp-block-k12-section rounded-4 bg-color-blue-t1-community">
                    <div className="row">
                      <div className="col-12 g-0">
                        {loggedInUser?.role === "LEARNING_COACH" && showLearningCoachCommunity && (
                        <div className="wp-block-k12-cta-blade h-50 mb-0 lc-community-block">
                          <div className="row">
                            <div className="col-12">
                              <div className="p-5">
                                <div className="d-flex gap-2 bottom center1">
                                  <div className="align-items-center1">
                                    <img decoding="async" src={communityIconImg} alt="" className="wp-image- img-fluid cta-icon" />
                                  </div>

                                  <div className="flex-grow-1 align-items-center1">
                                    <p className="eyebrow">K12 Community</p>
                                    <h2 className="">Connect with your community!</h2>
                                    <p className="">You're not alone! Whether online or out and about, you'll find new friends here.</p>

                                    <div className="btn-primary text-center1">
                                      <a href="https://community.k12.com/" data-type="page" data-id="249" target="_blank" rel="noreferrer noopener">Learn More</a>
                                    </div>
                                  </div>
                                </div>

                                <div className="wp-block-k12-user-role-content-wrapper lc-community-block">
                                  <div className="wp-block-k12-landing-community btn-primary m-0 lc-btn">
                                    <div className="row g-0 map-right">
                                      <div className="community-map-wrapper bg-color-white">
                                        <div className="col-md-12 col-xxl-6">
                                          <div className="body">
                                            <h3>Learning Coach Community</h3>
                                            <p>Here's where Learning Coaches and other supportive adults find targeted support and resources, access important school information in one place, find tips and tools for online school, and lots more!
                                              <br /><br />

                                              <ButtonLink
                                              urlKey="COMMUNITY_URL"
                                              label="Find your Community"
                                              className="external-link-btn"
                                              source="links"
                                              target="_blank"
                                              />
                                              
                                            </p>
                                          </div>
                                        </div>
                                        <div className="col-md-12 col-xxl-6">
                                          <div className="community-map-static" data-api_key="ZTo3xJfov4VR8FOAL8GM">
                                            <figure className="wp-block-image mb-0">
                                              <img
                                                decoding="async"
                                                loading="lazy"
                                                width="1030"
                                                height="590"
                                                src={learningCommunityImg}
                                                alt="Learning Coach Map"
                                                className="wp-image-506 img-fluid"
                                              />
                                            </figure>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                              </div>
                            </div>


                          </div>
                        </div>
                        )}

                        {loggedInUser?.role === "STUDENT" && showK12Zone && (
                        <div className="wp-block-k12-user-role-content-wrapper">
                          <div className="wp-block-k12-section container-fluid bg-transparent mb-0 pt-5 k12-zone-block">
                            <div className="row">
                              <div className="col-12 g-0">
                                <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-3 wp-block-columns-is-layout-flex">
                                  <div className="wp-block-column ps-5 ms-3 is-layout-flow wp-block-column-is-layout-flow">
                                    <p className="eyebrow">
                                      <strong>FEATURING</strong>
                                    </p>
                                    <h2 className="wp-block-heading">K12 Zone</h2>
                                    <p>
                                      Ready to get social? Experience our virtual, open-world campus where you can hang out,
                                      chat with friends, and join clubs and activities in a safe, welcoming environment.
                                    </p>
                                    <div className="wp-block-buttons btn-primary is-layout-flex wp-block-buttons-is-layout-flex">
                                      <div className="wp-block-button btw-link">
                                        <a className="wp-block-button__link wp-element-button" href="/community/k12-zone/">
                                          Learn More
                                        </a>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="wp-block-column pe-5 is-layout-flow wp-block-column-is-layout-flow">
                                    <div className="vidyard-video-block">
                                      <div className="row">
                                        <div style={{ display: "block", opacity: 1 }}>
                                          <VidyardPlayer uuid="FLbQGoUFC2UBVv7uEay7WY" type="lightbox" />
                                        </div>

                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}

                      </div>
                    </div>
                  </div>
             
              </div>
             

              <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                <div className="wp-block-k12-section bg-color-light-gray-2 rounded-4">
                  <div className="row">
                    <div className="col-12 g-0">
                      <div className="wp-block-k12-cta-blade">
                        <div className="row">
                          <div className="col-12">
                            <div className="p-5">
                              <div className="d-flex gap-2 bottom-center padding-content">
                                <div className="align-items-center">
                                  <img decoding="async" src={contactIconImg} alt="contact" className="wp-image- img-fluid cta-icon" />

                                </div>

                                <div className="flex-grow-1 align-items-center">
                                  <p className="eyebrow">Contact</p>
                                  <h2 className="">Need a little help?</h2>
                                  <p className="">Call, chat, or find answers in our FAQs. We're here for you!</p>

                                  <div className="btn-primary text-center">
                                    <a href="/contact/" data-type="page" data-id="255">Contact Us</a>
                                  </div>

                                </div>

                              </div>

                              <img decoding="async" src={contactImg} alt="" className="wp-image-432 img-fluid bottom-center" />

                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    </div>


  );
}
